// The shared MOTION TUNING for every route transition in the app — both KINDS of them.
//
// A route transition is the mechanism: manufacturing an interruptible middle between two routes. It comes in
// two kinds, and the industry names them the same way Apple and Material do:
//
//   • LATERAL      — movement between PEERS at the same level of hierarchy (a tab strip, the section pager).
//                    Apple calls this "flat navigation"; Material calls it "lateral navigation". The pages sit
//                    side by side and travel at the same speed; there is no depth, so no parallax and no cast
//                    shadow — just a hairline seam.
//   • HIERARCHICAL — movement between LEVELS (a list → the item it opens). Apple calls this "hierarchical
//                    navigation"; Material calls it "forward and backward navigation". The arriving page rides
//                    ON TOP, the one underneath trails at `parallax` of the travel and is dimmed, and the
//                    leading edge casts a shadow. Its two operations are the stack's: PUSH and POP.
//
// Both kinds are implemented twice over, and every implementation reads its numbers from here so they all feel
// like one system:
//   • ../surface  — a transition SURFACE: an ordered group of routes rendered as sliding panes. Runs in either
//                   kind (`kind: 'lateral' | 'hierarchical'`).
//   • ../stack    — the NAV STACK: URL-depth-driven stacked screens on a phone. Hierarchical by construction.
//
// Kept in `core/` because it is the one thing both of them (and the bottom sheet's travel, see
// lib/spring-travel.ts) depend on. Nothing here knows about routes, panes or the DOM.

export const TRANSITION_CONFIG = {
  /** Fraction of the surface width past which a release commits (below it, springs back). */
  commitThreshold: 0.4,
  /** Width (px) of the edge strip that starts a gesture on an `edgeOnly` surface. */
  edgeWidth: 28,
  /** Movement (px) before we lock the gesture to horizontal (and reject it as a vertical scroll). */
  intentSlop: 6,
  /** How far the underneath layer trails the top layer, as a fraction of its own travel — the HIERARCHICAL
   *  depth cue. Lateral surfaces travel both panes at full speed and never read this. */
  parallax: 0.28,
  /** Release speed (px/ms) in the commit direction that commits regardless of how far the drag got. */
  flickVelocity: 0.5,
  /** Spring driving the release/glide settle. Stiff and slightly UNDER-damped (ζ≈0.85) so it arrives fast
   *  with no soft tail. Shared with the link-glide sampling in spring.ts. */
  spring: { stiffness: 260, damping: 27, mass: 1 },
  /** The soft shadow the top pane casts along its leading edge — HIERARCHICAL only. `peakAlpha` is a PEAK,
   *  not a constant. */
  shadow: { peakAlpha: 0.14, width: 36 },
} as const
